import React, { useId, useMemo, useState } from 'react';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { formatNumber } from '../../services/csvDataService';
import {
	CHART_VARIANTS,
	computeMancheRunningAveragePoints,
	computePartiesAverage,
	computeScoreDistribution,
	computeSequentialParties,
	computeSeriesPartyStats,
	describeDonutSlice,
	buildSeasonYearColorMap,
	resolvePartyBarColor,
	computeSeasonYearLabelPositions,
	buildYAxis,
	buildMancheAvgStepPath,
} from '../../utils/playerStatsCharts';

function maxPartySlotsFromRounds(rounds) {
	let slots = 0;
	(rounds ?? []).forEach((round) => {
		if (Number(round.quilles1) > 0) slots = Math.max(slots, 1);
		if (Number(round.quilles2) > 0) slots = Math.max(slots, 2);
		if (Number(round.quilles3) > 0) slots = Math.max(slots, 3);
		if (Number(round.quilles4) > 0) slots = Math.max(slots, 4);
	});
	return slots;
}

function formatThresholdLabel(value) {
	if (Number.isInteger(value)) return String(value);
	return value.toFixed(1).replace('.', ',');
}

function getTeamScoreBucketsForRounds(rounds, baseBuckets) {
	const slots = maxPartySlotsFromRounds(rounds);
	if (slots !== 2 || !Array.isArray(baseBuckets) || baseBuckets.length < 4) {
		return baseBuckets;
	}

	const t1 = 160 / 2;
	const t2 = 185 / 2;
	const t3 = 210 / 2;

	return [
		{
			id: 't0-2',
			label: `< ${formatThresholdLabel(t1)}`,
			color: baseBuckets[0].color,
			colorEnd: baseBuckets[0].colorEnd,
			match: (s) => s < t1,
		},
		{
			id: 't1-2',
			label: `${formatThresholdLabel(t1)} – ${formatThresholdLabel(t2)}`,
			color: baseBuckets[1].color,
			colorEnd: baseBuckets[1].colorEnd,
			match: (s) => s >= t1 && s <= t2,
		},
		{
			id: 't2-2',
			label: `${formatThresholdLabel(t2)} – ${formatThresholdLabel(t3)}`,
			color: baseBuckets[2].color,
			colorEnd: baseBuckets[2].colorEnd,
			match: (s) => s > t2 && s <= t3,
		},
		{
			id: 't3-2',
			label: `> ${formatThresholdLabel(t3)}`,
			color: baseBuckets[3].color,
			colorEnd: baseBuckets[3].colorEnd,
			match: (s) => s > t3,
		},
	];
}

function SequentialPartiesChart({ rounds, overallAverage, config }) {
	const points = useMemo(() => computeSequentialParties(rounds), [rounds]);
	const mancheAvgPoints = useMemo(
		() => computeMancheRunningAveragePoints(points),
		[points],
	);
	const avg = useMemo(
		() => computePartiesAverage(points) || overallAverage || 0,
		[points, overallAverage],
	);
	const seasonColorMap = useMemo(
		() => buildSeasonYearColorMap(points),
		[points],
	);
	const showSeasonYear = seasonColorMap.size > 0;

	if (!points.length) return null;

	const runningAvgs = mancheAvgPoints.map((pt) => pt.runningAvg);
	const { minY, maxY, ticks } = buildYAxis(
		points.map((p) => p.value),
		runningAvgs,
	);
	const yRange = maxY - minY || 10;
	const barW = 22;
	const gap = 5;
	const groupGap = 12;
	const pad = { top: 28, right: 16, bottom: showSeasonYear ? 48 : 36, left: 40 };
	const chartH = 168;

	let x = pad.left;
	const bars = [];
	let prevManche = null;
	let prevYear = null;

	points.forEach((pt) => {
		if (prevYear !== null && pt.seasonYear !== prevYear) {
			x += groupGap;
		} else if (prevManche !== null && pt.manche !== prevManche) {
			x += groupGap;
		}
		bars.push({ ...pt, x, w: barW });
		x += barW + gap;
		prevManche = pt.manche;
		prevYear = pt.seasonYear;
	});

	const width = Math.max(320, x + pad.right);
	const height = pad.top + chartH + pad.bottom;
	const y = (v) => pad.top + chartH - ((v - minY) / yRange) * chartH;
	const lineMarkers = mancheAvgPoints.map((pt) => ({
		...pt,
		bar: bars[pt.barIndex],
	}));
	const stepPath = buildMancheAvgStepPath(lineMarkers, (v) => y(v));

	const seasonYearLabels = showSeasonYear
		? computeSeasonYearLabelPositions(bars)
		: [];

	return (
		<div className="nr-chart nr-chart--bars">
			<div className="nr-chart-head">
				<h4 className="nr-chart-title">{config.barsTitle}</h4>
				<span className="nr-chart-badge">Moy. {formatNumber(avg, 1)}</span>
			</div>
			<div className="nr-chart-scroll nr-chart-scroll--bars">
				<svg
					className="nr-chart-svg nr-chart-svg--bars"
					width={width}
					height={height}
					viewBox={`0 0 ${width} ${height}`}
					preserveAspectRatio="xMinYMid meet"
					role="img"
					aria-label="Scores par partie avec moyenne cumulative par manche"
				>
					{ticks.map((tick) => (
						<g key={tick} className="nr-grid-line">
							<line
								x1={pad.left}
								y1={y(tick)}
								x2={width - pad.right}
								y2={y(tick)}
							/>
							<text x={pad.left - 8} y={y(tick) + 4} textAnchor="end">
								{tick}
							</text>
						</g>
					))}

					{bars.map((bar, index) => {
						const barH = ((bar.value - minY) / yRange) * chartH;
						const bx = bar.x;
						const by = y(bar.value);
						const barKey = `${bar.seasonYear ?? 's'}-${bar.label}-${index}`;
						return (
							<g key={barKey} className="nr-bar-group">
								<rect
									className="nr-bar"
									x={bx}
									y={by}
									width={bar.w}
									height={barH}
									rx="6"
									fill={resolvePartyBarColor(bar, seasonColorMap)}
								/>
								<text className="nr-bar-value" x={bx + bar.w / 2} y={by - 6} textAnchor="middle">
									{bar.value}
								</text>
								<text
									className="nr-bar-label"
									x={bx + bar.w / 2}
									y={height - (showSeasonYear ? 22 : 10)}
									textAnchor="middle"
								>
									{bar.label}
								</text>
							</g>
						);
					})}

					{seasonYearLabels.map(({ year, x: yearX }) => (
						<text
							key={year}
							className="nr-bar-year nr-bar-year--group"
							x={yearX}
							y={height - 10}
							textAnchor="middle"
						>
							{year}
						</text>
					))}

					{lineMarkers.length > 0 && (
						<g className="nr-running-avg-line">
							{stepPath && (
								<path
									d={stepPath}
									fill="none"
									stroke="#FF6B6B"
									strokeWidth="2"
									strokeDasharray="5 4"
									strokeLinejoin="round"
									strokeLinecap="round"
								/>
							)}
							{lineMarkers.map((pt) => (
								<circle
									key={`avg-${pt.label}`}
									cx={pt.bar.x + pt.bar.w / 2}
									cy={y(pt.runningAvg)}
									r="3"
									fill="#FF6B6B"
								>
									<title>{`${pt.label} — moy. ${formatNumber(pt.runningAvg, 1)}`}</title>
								</circle>
							))}
						</g>
					)}
				</svg>
			</div>
		</div>
	);
}

function DonutSliceModal({ slice, total, onClose }) {
	if (!slice) return null;

	return (
		<Dialog
			open
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			className="new-results-modal nr-donut-slice-modal"
		>
			<DialogTitle className="new-results-modal-title">
				<span>{`Plage parties : ${slice.label}`}</span>
				<IconButton aria-label="Fermer" onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers className="nr-donut-slice-modal-body">
				<div className="nr-donut-legend">
					<div
						className="nr-donut-legend-swatch"
						style={{ background: `linear-gradient(135deg, ${slice.color}, ${slice.colorEnd})` }}
					/>
					<dl className="nr-donut-legend-details">
						<div>
							<dt>Parties</dt>
							<dd>{formatNumber(slice.count)}/{formatNumber(total)}</dd>
						</div>
						<div className="nr-donut-legend-percent">
							<dd>{slice.percent} %</dd>
						</div>
					</dl>
				</div>
			</DialogContent>
		</Dialog>
	);
}

function SeriesBarsHelpModal({ open, onClose, slotLabelsText }) {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			className="new-results-modal nr-series-help-modal"
		>
			<DialogTitle className="new-results-modal-title">
				<span>{`Lecture de la jauge ${slotLabelsText || 'parties'}`}</span>
				<IconButton aria-label="Fermer" onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers className="nr-series-help-modal-body">
				<p>
					La jauge utilise toute la hauteur : la meilleure partie remplit 100 %. Le trait pointillé
					indique l&apos;équilibre (moyenne saison).
				</p>
				<ul className="nr-series-help-list">
					<li>Au-dessus du trait : quilles gagnées.</li>
					<li>En dessous du trait : quilles perdues.</li>
					<li>Le nombre au centre de la barre est la moyenne de la partie.</li>
				</ul>
			</DialogContent>
		</Dialog>
	);
}

function DistributionDonutChart({
	title,
	slices,
	gradientBuckets,
	centerValue,
	centerLabel,
	ariaLabel,
	sliceAriaLabel,
	modalTotal,
}) {
	const gradId = useId().replace(/:/g, '');
	const [selectedSlice, setSelectedSlice] = useState(null);
	const total = slices.reduce((s, sl) => s + sl.count, 0);

	if (!total) {
		return (
			<div className="nr-chart nr-chart--donut">
				<h4 className="nr-chart-title">{title}</h4>
				<p className="new-results-hint">Pas assez de données.</p>
			</div>
		);
	}

	const size = 200;
	const cx = size / 2;
	const cy = size / 2;
	const rOuter = 82;
	const rInner = 54;
	const gap = 0.05;

	let angle = -Math.PI / 2;
	const arcs = slices.map((slice) => {
		const sliceAngle = (slice.count / total) * (Math.PI * 2 - gap * slices.length);
		const start = angle + gap / 2;
		const end = angle + sliceAngle + gap / 2;
		angle += sliceAngle + gap;
		const bucket = gradientBuckets.find((b) => b.id === slice.id);
		return {
			...slice,
			color: slice.color ?? bucket?.color ?? '#888',
			colorEnd: slice.colorEnd ?? bucket?.colorEnd ?? bucket?.color ?? '#888',
			path: describeDonutSlice(cx, cy, rOuter, rInner, start, end),
		};
	});

	const handleSliceClick = (event, slice) => {
		event.stopPropagation();
		setSelectedSlice(slice);
	};

	return (
		<div className="nr-chart nr-chart--donut">
			<h4 className="nr-chart-title">{title}</h4>
			<div className="nr-donut-layout nr-donut-layout--solo">
				<svg
					className="nr-donut-svg"
					viewBox={`0 0 ${size} ${size}`}
					role="img"
					aria-label={ariaLabel}
				>
					<defs>
						{gradientBuckets.map((b) => (
							<linearGradient
								key={b.id}
								id={`${gradId}-${b.id}`}
								x1="0%"
								y1="0%"
								x2="100%"
								y2="100%"
							>
								<stop offset="0%" stopColor={b.color} />
								<stop offset="100%" stopColor={b.colorEnd} />
							</linearGradient>
						))}
						<filter id={`${gradId}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
							<feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
						</filter>
					</defs>

					<circle cx={cx} cy={cy} r={rOuter + 6} className="nr-donut-halo" />

					{arcs.map((sl) => (
						<path
							key={sl.id}
							d={sl.path}
							fill={`url(#${gradId}-${sl.id})`}
							filter={`url(#${gradId}-shadow)`}
							className={`nr-donut-slice${selectedSlice?.id === sl.id ? ' nr-donut-slice--active' : ''}`}
							onClick={(event) => handleSliceClick(event, sl)}
							role="button"
							tabIndex={0}
							aria-label={sliceAriaLabel(sl)}
							onKeyDown={(event) => {
								if (event.key === 'Enter' || event.key === ' ') {
									event.preventDefault();
									handleSliceClick(event, sl);
								}
							}}
						/>
					))}

					<circle cx={cx} cy={cy} r={rInner - 2} className="nr-donut-hole" />

					<text x={cx} y={cy - 5} textAnchor="middle" className="nr-donut-center-value">
						{centerValue}
					</text>
					<text x={cx} y={cy + 13} textAnchor="middle" className="nr-donut-center-label">
						{centerLabel}
					</text>
				</svg>
			</div>

			<DonutSliceModal
				slice={selectedSlice}
				total={modalTotal}
				onClose={() => setSelectedSlice(null)}
			/>
		</div>
	);
}

function ScoreDonutChart({ rounds, config, variant }) {
	const scoreBuckets = useMemo(() => {
		if (variant === 'team') {
			return getTeamScoreBucketsForRounds(rounds, config.scoreBuckets);
		}
		return config.scoreBuckets;
	}, [variant, rounds, config.scoreBuckets]);

	const slices = useMemo(
		() => computeScoreDistribution(rounds, scoreBuckets),
		[rounds, scoreBuckets],
	);
	const total = slices.reduce((s, sl) => s + sl.count, 0);

	return (
		<DistributionDonutChart
			title={config.donutTitle}
			slices={slices}
			gradientBuckets={scoreBuckets}
			centerValue={total}
			centerLabel="parties"
			ariaLabel="Répartition des scores par tranche"
			sliceAriaLabel={(sl) => `${sl.label} : ${sl.count} parties (${sl.percent} %)`}
			modalTotal={total}
		/>
	);
}

function SeriesPartyBarsChart({ rounds, config, fairAverage }) {
	const stats = useMemo(
		() => computeSeriesPartyStats(rounds, config.seriesPartyBuckets, fairAverage),
		[rounds, config.seriesPartyBuckets, fairAverage],
	);
	const [helpOpen, setHelpOpen] = useState(false);
	const averageLabel = Number(stats.fairAverage).toFixed(2);
	const maxLabel = Number(stats.maxAverage).toFixed(2);
	const slotLabels = stats.slots.map((slot) => slot.label);
	const slotLabelsText = slotLabels.join(' · ');
	const barsTitle = slotLabelsText
		? `${slotLabelsText} — vs équilibre`
		: config.seriesPartyBarsTitle;
	const barsAriaLabel = slotLabels.length > 0
		? `Écart des moyennes ${slotLabels.join(' ')} par rapport à la moyenne saison`
		: 'Écart des moyennes par rapport à la moyenne saison';

	if (!stats.slots.length) {
		return (
			<div className="nr-chart nr-chart--series-bars">
				<h4 className="nr-chart-title">{barsTitle}</h4>
				<p className="new-results-hint">Pas assez de données.</p>
			</div>
		);
	}

	return (
		<div className="nr-chart nr-chart--series-bars">
			<div className="nr-chart-head">
				<div className="nr-chart-title-with-info">
					<h4 className="nr-chart-title">{barsTitle}</h4>
					<IconButton
						aria-label="Afficher l'explication de la jauge"
						size="small"
						className="nr-info-icon-btn"
						onClick={() => setHelpOpen(true)}
					>
						<InfoOutlinedIcon fontSize="inherit" />
					</IconButton>
				</div>
				<span className="nr-chart-badge nr-chart-badge--stacked">
					<span>Moyenne {averageLabel}</span>
					<span>Maximum {maxLabel}</span>
				</span>
			</div>

			<div
				className="nr-series-bars"
				role="img"
				aria-label={barsAriaLabel}
			>
				{stats.slots.map((slot) => {
						const fairPct = slot.fairBarPct;
						const barPct = slot.barPct;
						const lossHeight = Math.max(0, fairPct - barPct);
						const gainHeight = Math.max(0, barPct - fairPct);
						const baseFillHeight = Math.min(barPct, fairPct);

						return (
							<div key={slot.id} className="nr-series-bar-col">
								<div
									className="nr-series-bar"
									title={`${slot.label} : moy. ${formatNumber(slot.average, 1)} (équilibre ${formatNumber(stats.fairAverage, 1)})`}
								>
									<div className="nr-series-bar-body">
										<div
											className="nr-series-bar-fair-line"
											style={{ bottom: `${fairPct}%` }}
											aria-hidden="true"
										/>
										{lossHeight > 0.5 && (
											<div
												className="nr-series-bar-loss"
												style={{
													bottom: `${barPct}%`,
													height: `${lossHeight}%`,
												}}
												title={`Perdu : ${formatNumber(slot.deltaAvg, 1)} vs équilibre`}
											/>
										)}
										{baseFillHeight > 0 && (
											<div
												className="nr-series-bar-fill"
												style={{
													height: `${baseFillHeight}%`,
													background: `linear-gradient(180deg, ${slot.color}, ${slot.colorEnd})`,
												}}
											/>
										)}
										{gainHeight > 0.5 && (
											<div
												className="nr-series-bar-gain"
												style={{
													bottom: `${fairPct}%`,
													height: `${gainHeight}%`,
													background: `linear-gradient(180deg, ${slot.colorEnd}, ${slot.color})`,
												}}
												title={`Gagné : +${formatNumber(slot.deltaAvg, 1)} vs équilibre`}
											/>
										)}
										<span className="nr-series-bar-avg nr-series-bar-avg--center">
											{formatNumber(slot.average, 1)}
										</span>
									</div>
								</div>
								<span className="nr-series-bar-label">{slot.label}</span>
								{slot.isGain && (
									<span className="nr-series-bar-delta nr-series-bar-delta--gain">
										+{formatNumber(slot.deltaAvg, 1)}
									</span>
								)}
								{slot.isLoss && (
									<span className="nr-series-bar-delta nr-series-bar-delta--loss">
										{formatNumber(slot.deltaAvg, 1)}
									</span>
								)}
								{slot.isBalanced && (
									<span className="nr-series-bar-delta">≈ équilibre</span>
								)}
							</div>
					);
				})}
			</div>
			<SeriesBarsHelpModal
				open={helpOpen}
				onClose={() => setHelpOpen(false)}
				slotLabelsText={slotLabelsText}
			/>
		</div>
	);
}

export default function PlayerStatsCharts({ rounds, overallAverage, variant = 'player' }) {
	if (!rounds?.length) return null;

	const config = CHART_VARIANTS[variant] ?? CHART_VARIANTS.player;

	return (
		<div className="nr-charts">
			<SequentialPartiesChart
				rounds={rounds}
				overallAverage={overallAverage}
				config={config}
			/>
			<div className={`nr-charts-donuts${variant === 'player' ? ' nr-charts-donuts--pair' : ''}`}>
				<ScoreDonutChart rounds={rounds} config={config} variant={variant} />
				{variant === 'player' && (
					<SeriesPartyBarsChart
						rounds={rounds}
						config={config}
						fairAverage={overallAverage}
					/>
				)}
			</div>
		</div>
	);
}
